/**
 * CAOS-470 — the signed-in person's preferences, kept by the kernel (`preferences get|set`) instead of
 * browser storage, so a choice follows the person to any browser or device.
 *
 * The workspace loads them once the person is signed in, reads them synchronously from this cache,
 * and writes each change through to the kernel. Before sign-in nothing is loaded and nothing is
 * stored: a change made then lasts only for the page, and the theme follows the operating system.
 *
 * Interface state that should NOT follow a person (Setup's recent sections) is not kept here.
 */

export interface PreferencesFetchResponse {
  json(): Promise<unknown>;
}
export type PreferencesFetch = (path: string, init?: { method?: string; body?: unknown }) => Promise<PreferencesFetchResponse>;

/** Fired on `document` whenever the cached preferences change, with the keys that changed. */
export const PREFERENCES_CHANGE_EVENT = 'caos-preferences-change';

const defaultFetch: PreferencesFetch = (path, init) =>
  fetch(path, {
    credentials: 'same-origin',
    cache: 'no-store',
    method: init?.method ?? 'GET',
    ...(init?.body !== undefined ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(init.body) } : {}),
  });

let doFetch: PreferencesFetch = defaultFetch;
let values: Record<string, unknown> = {};
/**
 * CAOS-749 — the last value the KERNEL confirmed for each key: what a refused write goes back to.
 *
 * Not "whatever the cache held a moment ago". Two writes in a row, the first refused, would restore
 * the first one's optimistic value — a value the kernel never stored — and the screen would settle on
 * something that is nowhere. This is the only honest answer to "what is actually stored", and it is
 * updated exactly where the kernel tells us: a load, and a write it accepted.
 */
let confirmed: Record<string, unknown> = {};
/** What the person would see for a key they cleared: the org default, else a platform default the kernel resolved. */
let inherited: Record<string, unknown> = {};
let loaded = false;
/** Writes go out one at a time, so the kernel stores them in the order the person made them. */
let writes: Promise<unknown> = Promise.resolve();
let writeCount = 0;
/** The newest write per key; an older write's answer must not replace a newer choice. */
const latestWrite = new Map<string, number>();

type Values = Record<string, unknown>;
interface Envelope {
  ok?: boolean;
  data?: { resolved?: Values; own?: Values; defaults?: Values };
}

const isValues = (v: unknown): v is Values => v != null && typeof v === 'object' && !Array.isArray(v);
const has = (o: Values, key: string): boolean => Object.prototype.hasOwnProperty.call(o, key);

function announce(keys: readonly string[]): void {
  if (keys.length === 0) return;
  document.dispatchEvent(new CustomEvent(PREFERENCES_CHANGE_EVENT, { detail: { keys } }));
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** Seam for tests and hosts with their own transport. */
export function setPersonPreferencesFetch(fn: PreferencesFetch): void {
  doFetch = fn;
}

/** Load the signed-in person's resolved preferences. Resolves false, changing nothing, when that fails. */
export async function loadPersonPreferences(): Promise<boolean> {
  try {
    const env = (await (await doFetch('/api/preferences/get')).json()) as Envelope;
    if (env?.ok !== true || !isValues(env.data?.resolved)) return false;
    const previous = values;
    const resolved = env.data.resolved;
    const own = isValues(env.data.own) ? env.data.own : {};
    const defaults = isValues(env.data.defaults) ? env.data.defaults : {};
    values = { ...resolved };
    confirmed = { ...resolved };
    // Where the person has not chosen, what they see IS what they inherit; where they have, the org
    // default is what shows once they clear it.
    inherited = { ...Object.fromEntries(Object.entries(resolved).filter(([k]) => !has(own, k))), ...defaults };
    loaded = true;
    const keys = new Set([...Object.keys(previous), ...Object.keys(values)]);
    announce([...keys].filter((k) => !same(previous[k], values[k])));
    return true;
  } catch {
    return false;
  }
}

export function personPreferencesLoaded(): boolean {
  return loaded;
}

/** The current value of one preference, or undefined when there is none. */
export function preference<T = unknown>(key: string): T | undefined {
  return values[key] as T | undefined;
}

/** Every cached preference key beginning with `prefix`. */
export function preferenceKeys(prefix: string): string[] {
  return Object.keys(values).filter((k) => k.startsWith(prefix));
}

/**
 * Change one preference. `null` clears the person's own value, and what they inherit (the org default,
 * else the platform default) shows at once. The cache changes immediately so the screen responds; once
 * the kernel has stored the write, the value it resolves for that key replaces the cached one, unless
 * the person has changed the key again since. Resolves false when nothing was stored: not signed in, or
 * the kernel refused — and in that case the cache goes back to what it held, and says so, rather than
 * keeping a value the kernel never stored (CAOS-749).
 */
export function setPreference(key: string, value: unknown): Promise<boolean> {
  if (value !== null) values[key] = value;
  else if (has(inherited, key)) values[key] = inherited[key];
  else delete values[key];
  announce([key]);
  if (!loaded) return Promise.resolve(false);
  const id = ++writeCount;
  latestWrite.set(key, id);
  /**
   * CAOS-749 — put back what the KERNEL holds, unless the person has chosen again since: their
   * newer choice wins, and its own answer will settle the key.
   *
   * Read when the refusal arrives, not snapshotted when the write left. A write made BEFORE this one
   * can be accepted while this one is still in flight, which moves what the kernel holds; a snapshot
   * taken at the top would put back the value it held before that, and the screen would settle on
   * something the kernel had already replaced. The cache is what every control reads, so that
   * disagreement would stand until the next full load with nothing announcing it.
   */
  const restore = (): void => {
    if (latestWrite.get(key) !== id) return;
    if (has(confirmed, key)) values[key] = confirmed[key];
    else delete values[key];
    announce([key]);
  };
  const result = writes.then(async () => {
    try {
      const env = (await (await doFetch('/api/preferences/set', { method: 'POST', body: { key, value } })).json()) as Envelope;
      if (env?.ok !== true || !isValues(env.data?.resolved)) {
        restore();
        return false;
      }
      const stored = env.data.resolved[key];
      // What the kernel holds now — recorded whether or not the person has chosen again since. The
      // newer choice governs the SCREEN, but it does not change what is stored, and a refusal of
      // that newer choice has to fall back to this write's result rather than to whatever preceded
      // it. Writes go out one at a time, so the last answer to arrive is the current one.
      if (stored === undefined) delete confirmed[key];
      else confirmed[key] = stored;
      if (latestWrite.get(key) === id && !same(values[key], stored)) {
        if (stored === undefined) delete values[key];
        else values[key] = stored;
        announce([key]);
      }
      return true;
    } catch {
      restore();
      return false;
    }
  });
  writes = result;
  return result;
}

/** Resolves once every write made so far has been answered. */
export async function whenPreferencesSaved(): Promise<void> {
  await writes;
}

/** Forget everything loaded, for tests and sign-out. */
export function resetPersonPreferences(): void {
  values = {};
  confirmed = {};
  inherited = {};
  loaded = false;
  doFetch = defaultFetch;
  writes = Promise.resolve();
  writeCount = 0;
  latestWrite.clear();
}
